import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { Tooltip } from 'antd';
import type { ApolloError } from '@apollo/client';
import { useSidebarSiteQuery, SidebarSiteQuery } from '@/.generated/graphql';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';

/** Common item shape passed to antd Menu / Collapse. The trees build their
 * own labels per module — this is just the antd-compatible structure. */
export type MenuItem = {
  key: string;
  label: string | React.ReactNode;
  icon?: React.ReactNode;
  children?: MenuItem[];
  className?: string;
};

/** Raw access-filtered site tree shared by every module. The trees consume
 * this and build their own module-specific antd Menu items. */
export type RawSite = NonNullable<SidebarSiteQuery['sites']>['data'][number];
export type RawBuilding = NonNullable<RawSite['buildings']>[number];
export type RawFloor = NonNullable<RawBuilding['floors']>[number];
export type RawRoom = NonNullable<RawFloor['rooms']>[number];
export type RawZone = NonNullable<RawFloor['zones']>[number];

/** Combined room-or-zone (matches the legacy `roomszones` field shape — rooms
 * with their nested zones, plus standalone floor zones). */
export type RoomZoneItem =
  | (RawRoom & { kind: 'room'; zones: RawZone[] })
  | (RawZone & { kind: 'zone' });

export type FilteredFloor = Omit<RawFloor, 'rooms' | 'zones'> & {
  roomszones: RoomZoneItem[];
};
export type FilteredBuilding = Omit<RawBuilding, 'floors'> & {
  floors: FilteredFloor[];
};
export type FilteredSite = Omit<RawSite, 'buildings'> & {
  buildings: FilteredBuilding[];
};

export const MENU_STYLE = { borderRight: '0px' } as const;

/** Shared antd theme for the Monitor / Studio / Devices Spaces trees.
 * Hoisted to a module-level constant so the object identity is stable
 * across renders — otherwise antd's ConfigProvider re-derives Menu
 * tokens on every re-render of the tree. */
export const SIDEBAR_TREE_THEME = {
  components: {
    Menu: {
      itemBorderRadius: 9999,
      itemMarginBlock: 2,
      itemHeight: 36,
    },
  },
} as const;

/** Sentinel key for the "All Spaces" selectable item in Devices mode. */
export const ALL_SPACES_KEY = '__all_spaces__';

// `name` is `string | null | undefined` upstream — coerce to '' so the
// localeCompare call never throws on nameless nodes that survived the
// filter (the filter pipeline now keeps them on empty search).
const naturalSort = (
  a: { name: string | null | undefined },
  b: { name: string | null | undefined },
) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true, sensitivity: 'base' });

/** Wraps a menu-item row in a Tooltip that anchors to the pill and appears
 * fully outside the pill to the right (clearing the row's hover-only edit
 * controls). The tooltip only opens if the inner content is actually
 * truncated — measured on hover via scrollWidth > clientWidth. */
export const EllipsisRowTooltip: React.FC<{ name: string; children: React.ReactNode }> = ({
  name,
  children,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [offsetX, setOffsetX] = useState(24);
  return (
    <Tooltip
      title={name}
      placement="right"
      arrow={false}
      open={open}
      align={{ offset: [offsetX, 0] }}
      onOpenChange={(visible) => {
        if (!visible) {
          setOpen(false);
          return;
        }
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        // Detect ellipsis-mediated truncation in the row label. Two known
        // emitters: antd's `Typography.Text` with `ellipsis` (renders
        // `.ant-typography-ellipsis`) and `MiddleTruncatedText`'s shrinkable
        // head span (Tailwind `overflow-hidden text-ellipsis`). Rather than
        // hard-coding both class names, walk descendants and check computed
        // style — only elements that actually have `text-overflow: ellipsis`
        // AND `overflow: hidden` count as label nodes, so layout-only
        // `overflow-hidden` flex wrappers don't trigger false positives.
        const candidates = Array.from(wrapper.querySelectorAll<HTMLElement>('*'));
        const truncated = candidates.some((el) => {
          if (el.scrollWidth <= el.clientWidth) return false;
          const cs = getComputedStyle(el);
          if (cs.textOverflow !== 'ellipsis') return false;
          return cs.overflow === 'hidden' || cs.overflowX === 'hidden';
        });
        if (!truncated) {
          setOpen(false);
          return;
        }
        const pill = wrapper.closest<HTMLElement>(
          '.ant-menu-item, .ant-menu-submenu-title, .ant-collapse-header',
        );
        if (pill) {
          const gap = pill.getBoundingClientRect().right - wrapper.getBoundingClientRect().right;
          setOffsetX(Math.max(0, gap) + 8);
        }
        setOpen(true);
      }}
    >
      <div ref={wrapperRef} style={{ width: '100%' }}>
        {children}
      </div>
    </Tooltip>
  );
};

/** Recursively trim a subtree so only the branch leading to selectedKey
 * survives. Used by Monitor (Site-level chain) and Studio (Building-level
 * chain). Returns null if selectedKey isn't under this node. */
export function trimToChain(node: MenuItem, selectedKey: string | null): MenuItem | null {
  if (!selectedKey) return null;
  if (node.key === selectedKey) return node;
  if (!node.children) return null;
  for (const child of node.children) {
    const trimmed = trimToChain(child, selectedKey);
    if (trimmed) return { ...node, children: [trimmed] };
  }
  return null;
}

/** Walk the tree and tag the item whose key === selectedKey with the
 * `sidebar-item-directly-selected` class so CSS can draw the selected pill
 * on it (covers leaf Menu.Items AND SubMenu titles — antd's native
 * itemSelectedBg only handles leaves). */
export function markSelected(
  items: MenuItem[] | undefined,
  selectedKey: string | null,
): MenuItem[] | undefined {
  if (!items || !selectedKey) return items;
  return items.map((item) => ({
    ...item,
    className: item.key === selectedKey ? 'sidebar-item-directly-selected' : item.className,
    children: item.children ? markSelected(item.children, selectedKey) : item.children,
  }));
}

/**
 * Spread this on a MenuItem definition to set `children` only when the
 * array is non-empty. antd Menu draws a chevron (and treats the row as
 * a SubMenu) whenever `children` is defined — even an empty `[]` —
 * which incorrectly suggests the row is expandable when in fact there
 * is nothing inside. Calling `...maybeChildren(arr)` makes the row
 * render as a leaf when `arr` is empty.
 */
export function maybeChildren(children: MenuItem[]): { children?: MenuItem[] } {
  return children.length > 0 ? { children } : {};
}

/** Collect every key in this MenuItem's subtree (the item itself + all
 * descendants). Used to filter the global `openKeys` down to a single
 * Site's subtree when each Site is rendered as its own antd Menu. */
export function collectAllKeys(item: MenuItem, into: Set<string> = new Set()): Set<string> {
  into.add(String(item.key));
  for (const child of item.children ?? []) collectAllKeys(child, into);
  return into;
}

/** Walk a trimmed `trimToChain` subtree and return every non-leaf key
 * EXCEPT `selectedKey`. Used to force the chain Menu fully open down to
 * (but not into) the selected item, so the breadcrumb path is visible
 * at a glance without expanding the selected leaf's own children. */
export function collectChainOpenKeys(item: MenuItem, selectedKey: string): string[] {
  const out: string[] = [];
  const walk = (node: MenuItem) => {
    if (!node.children || node.children.length === 0) return;
    if (node.key === selectedKey) return;
    out.push(String(node.key));
    for (const child of node.children) walk(child);
  };
  walk(item);
  return out;
}

/**
 * The "Show all floors" button hides only when the tree is exactly in the
 * canonical all-floors view: every Site and every Building expanded, and
 * NO Floor expanded. Anywhere else (a Site collapsed, a Building collapsed,
 * or a Floor expanded showing rooms/zones) the button stays visible so the
 * user always has a one-click way back to the flat all-floors layout.
 */
export function isInAllFloorsView(items: MenuItem[], openKeys: string[]): boolean {
  const open = new Set(openKeys);
  for (const site of items) {
    if (!open.has(String(site.key))) return false;
    for (const building of site.children ?? []) {
      if (!open.has(String(building.key))) return false;
      for (const floor of building.children ?? []) {
        if (open.has(String(floor.key))) return false;
      }
    }
  }
  return true;
}

/** Cheap structural fingerprint of the raw site tree. Walks only the fields
 * the filter pipeline and downstream consumers actually read (id, name, and
 * zone.roomId for the room/zone grouping). Replaces a full `JSON.stringify`
 * of the tree, which was serializing every nested object's fields on every
 * Apollo cache write — for an org with thousands of leaf nodes that shows
 * up on the render path of every page mounting the sidebar. */
function sitesFingerprint(sites: RawSite[] | undefined): string {
  if (!sites) return '';
  const parts: string[] = [];
  for (const s of sites) {
    parts.push('s', s.id, s.name ?? '');
    for (const b of s.buildings ?? []) {
      parts.push('b', b.id, b.name ?? '');
      for (const f of b.floors ?? []) {
        parts.push('f', f.id, f.name ?? '');
        for (const r of f.rooms ?? []) {
          parts.push('r', r.id, r.name ?? '');
        }
        for (const z of f.zones ?? []) {
          parts.push('z', z.id, z.name ?? '', z.roomId ?? '');
        }
      }
    }
  }
  return parts.join('|');
}

/** Stable raw-sites snapshot. Returns a `rawSites` reference whose identity
 * only changes when the structural fingerprint changes — Apollo cache writes
 * that touch unrelated fields (sensor stats, floor metadata, etc.) don't
 * churn downstream consumers.
 *
 * The capture is gated through `useMemo` rather than render-phase ref
 * mutation, so the snapshot is only taken at commit phase — safe under
 * StrictMode / concurrent rendering.
 *
 * Exported so callers that need multiple derived views (e.g.
 * `DefaultSpaceRedirector` running two `findFirstAccessibleFloor` lookups
 * with different scopes) can share one Apollo subscriber + one fingerprint
 * pass instead of paying for each derived view separately.
 */
export function useStableRawSites(): {
  rawSites: RawSite[] | undefined;
  error: ApolloError | undefined;
} {
  const { data: rawSiteData, error } = useSidebarSiteQuery({
    fetchPolicy: 'cache-and-network',
  });
  const rawSites = rawSiteData?.sites?.data;
  const fingerprint = useMemo(() => sitesFingerprint(rawSites), [rawSites]);
  // Pin identity to `fingerprint` rather than `rawSites` — the memo body
  // reads `rawSites` but we intentionally keep the previous value when the
  // structural fingerprint hasn't changed. eslint can't see that.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableRawSites = useMemo(() => rawSites, [fingerprint]);
  return { rawSites: stableRawSites, error };
}

/** Hook that fetches the user's site tree, filters by access, and returns
 * a stable reference (only changes when the actual data changes — Apollo
 * cache writes to floors/buildings during tab switches won't churn this). */
export function useFilteredSites(filterValue: string): {
  sites: FilteredSite[] | undefined;
  error: ApolloError | undefined;
} {
  const { rawSites, error: siteError } = useStableRawSites();

  // Only `hasAccess` is read in the memo body — destructuring `assignments`
  // / `isUnrestricted` here would force them into the memo's dep array and
  // re-run the entire filter pipeline on every SpaceAccessContext publish.
  const { hasAccess } = useSpaceAccess();

  const filteredSites = useMemo<FilteredSite[] | undefined>(() => {
    const isZoneAccessible = (zoneId: string) => hasAccess('zone', zoneId);
    const isRoomAccessible = (roomId: string) => hasAccess('room', roomId);
    const isFloorAccessible = (floorId: string) => hasAccess('floor', floorId);
    const isBuildingAccessible = (buildingId: string) => hasAccess('building', buildingId);

    const isFloorOrChildrenAccessible = (
      floorId: string,
      rooms: readonly { id: string }[] | null | undefined,
      zones: readonly { id: string }[] | null | undefined,
    ) => {
      if (isFloorAccessible(floorId)) return true;
      return (
        (rooms || []).some((r) => isRoomAccessible(r.id)) ||
        (zones || []).some((z) => isZoneAccessible(z.id))
      );
    };

    const isBuildingOrChildrenAccessible = (
      buildingId: string,
      floors:
        | readonly {
            id: string;
            rooms?: readonly { id: string }[] | null;
            zones?: readonly { id: string }[] | null;
          }[]
        | null
        | undefined,
    ) => {
      if (isBuildingAccessible(buildingId)) return true;
      return (floors || []).some(
        (f) =>
          isFloorAccessible(f.id) ||
          (f.rooms || []).some((r) => isRoomAccessible(r.id)) ||
          (f.zones || []).some((z) => isZoneAccessible(z.id)),
      );
    };

    const isSiteOrChildrenAccessible = (
      siteId: string,
      buildings:
        | readonly {
            id: string;
            floors?:
              | readonly {
                  id: string;
                  rooms?: readonly { id: string }[] | null;
                  zones?: readonly { id: string }[] | null;
                }[]
              | null;
          }[]
        | null
        | undefined,
    ) => {
      if (hasAccess('site', siteId)) return true;
      return (buildings || []).some((b) => isBuildingOrChildrenAccessible(b.id, b.floors));
    };

    const allSites = rawSites || [];
    const sites = allSites.filter((site) =>
      isSiteOrChildrenAccessible(site.id, site.buildings || []),
    );

    // Hoist once at the top of the memo body so we don't recompute the same
    // lowered string at every floor / building / site filter step inside
    // hot inner loops.
    const filterLower = filterValue.toLowerCase();
    // `name` is `string | null | undefined` upstream; `?.toLowerCase()`
    // short-circuits to `undefined` for nameless nodes, which would silently
    // drop them even when the filter is empty. Coerce to '' so empty filter
    // keeps everything (`''.includes('')` is true) and non-empty filter
    // correctly excludes nameless nodes (no substring to match).
    const matchesFilter = (name: string | null | undefined): boolean =>
      (name ?? '').toLowerCase().includes(filterLower);

    return sites
      .map<FilteredSite>((site) => ({
        ...site,
        buildings: (site.buildings ?? [])
          .filter((b) => isBuildingOrChildrenAccessible(b.id, b.floors || []))
          .map<FilteredBuilding>((building) => ({
            ...building,
            floors: (building.floors ?? [])
              .filter((floor) =>
                isFloorOrChildrenAccessible(floor.id, floor.rooms || [], floor.zones || []),
              )
              .map<FilteredFloor>((floor) => {
                const roomItems = (floor.rooms ?? [])
                  .filter((r) => isRoomAccessible(r.id))
                  .map((room) => ({
                    ...room,
                    kind: 'room' as const,
                    // Zones always narrow to matches only — the search bar's
                    // contract is "show exactly what contains the term", so
                    // even when the parent room matches we still hide its
                    // non-matching zones. Empty filter naturally keeps all
                    // zones because `matchesFilter` coerces null names to '',
                    // and `''.includes('')` is true.
                    zones: (floor.zones ?? [])
                      .filter((z) => z.roomId === room.id)
                      .filter((z) => isZoneAccessible(z.id))
                      .filter((z) => matchesFilter(z.name)),
                  }))
                  .filter((room) => matchesFilter(room.name) || room.zones.length > 0);
                const zoneItems = (floor.zones ?? [])
                  .filter((z) => !z.roomId || !isRoomAccessible(z.roomId))
                  .filter((z) => isZoneAccessible(z.id))
                  .filter((z) => matchesFilter(z.name))
                  .map((zone) => ({ ...zone, kind: 'zone' as const }));
                const combined: RoomZoneItem[] = [...roomItems, ...zoneItems];
                combined.sort(naturalSort);
                const { rooms: _r, zones: _z, ...rest } = floor;
                return { ...rest, roomszones: combined };
              })
              .filter((f) => matchesFilter(f.name) || f.roomszones.length > 0)
              .sort(naturalSort),
          }))
          .filter((b) => matchesFilter(b.name) || b.floors.length > 0)
          .sort(naturalSort),
      }))
      .filter((s) => matchesFilter(s.name) || s.buildings.length > 0)
      .sort(naturalSort);
  }, [rawSites, filterValue, hasAccess]);

  return { sites: filteredSites, error: siteError };
}

/** Collect every key in the menu tree that has children. Used by tree views
 * to auto-expand all branches when a search filter is active, so matched
 * descendants are visible without the user manually clicking each chevron. */
export function collectAllExpandableKeys(items: MenuItem[]): string[] {
  const keys: string[] = [];
  const walk = (nodes: MenuItem[]) => {
    for (const n of nodes) {
      if (n.children && n.children.length > 0) {
        keys.push(String(n.key));
        walk(n.children);
      }
    }
  };
  walk(items);
  return keys;
}

/** Result shape returned by `findFirstAccessibleFloor`.
 *
 * `roomId` / `zoneId` are present when the floor itself isn't accessible
 * but a leaf under it is — callers writing redirect URLs must include the
 * leaf id so `SpaceAccessGuard` lets the user through. Mutually exclusive
 * with each other; absent together when the floor has direct access. */
export type AccessibleFloorLanding = {
  siteId: string;
  buildingId: string;
  floorId: string;
  roomId?: string;
  zoneId?: string;
};

export type HasAccess = ReturnType<typeof useSpaceAccess>['hasAccess'];

/** First accessible landing target under the user's site tree.
 *
 * Walks the raw site tree with short-circuit access checks instead of
 * running the full `useFilteredSites` pipeline (room/zone access map +
 * name filter + natural-sort × 4 levels). Cheap to call from non-rendering
 * consumers like `DefaultSpaceRedirector`.
 *
 * Each level is walked in `naturalSort` order so the landing target is
 * deterministic regardless of upstream API order — matches the
 * alphabetical surface the sidebar tree shows. Sort cost is negligible
 * (typical counts <~ 10 per level) and dwarfed by the room/zone
 * access-filter pipeline this function lets the caller skip.
 *
 * If the chosen floor has direct access, the result names only the
 * site / building / floor. If access comes from a leaf grant (room or
 * zone), the result also names the first accessible leaf at that floor —
 * callers writing the redirect URL must include the leaf id or the user
 * will be rejected by `SpaceAccessGuard` when they land on a bare floor
 * URL they don't have floor-level access to.
 *
 * - `preferSiteId`: try this site first; fall back to other sites in
 *   `naturalSort` order if it has no accessible landing. Use it to honour
 *   explicit `?siteId=…` deep links so a shared URL lands on the intended
 *   site.
 * - `buildingId`: scope the search to a specific building. Returns null
 *   if the building isn't in the tree or has no accessible landing.
 *
 * Pure function — no React hooks. Callers wrap it in `useMemo` with
 * `useStableRawSites().rawSites` + `useSpaceAccess().hasAccess` as deps.
 */
export function findFirstAccessibleFloor(
  rawSites: RawSite[] | undefined,
  hasAccess: HasAccess,
  options: { preferSiteId?: string | null; buildingId?: string | null } = {},
): AccessibleFloorLanding | null {
  if (!rawSites || rawSites.length === 0) return null;
  const { preferSiteId, buildingId } = options;

  // Returns the first accessible landing inside `building`, walking floors
  // and (when needed) per-floor rooms then zones in natural order. Rooms
  // are preferred over zones at the leaf level so a user with mixed
  // room/zone grants lands on the room-scoped URL — rooms aggregate more
  // canonical dashboard data than zones do.
  const findLandingInBuilding = (
    site: RawSite,
    building: NonNullable<RawSite['buildings']>[number],
  ): AccessibleFloorLanding | null => {
    for (const floor of [...(building.floors ?? [])].sort(naturalSort)) {
      if (hasAccess('floor', floor.id)) {
        return { siteId: site.id, buildingId: building.id, floorId: floor.id };
      }
      for (const room of [...(floor.rooms ?? [])].sort(naturalSort)) {
        if (hasAccess('room', room.id)) {
          return {
            siteId: site.id,
            buildingId: building.id,
            floorId: floor.id,
            roomId: room.id,
          };
        }
      }
      for (const zone of [...(floor.zones ?? [])].sort(naturalSort)) {
        if (hasAccess('zone', zone.id)) {
          return {
            siteId: site.id,
            buildingId: building.id,
            floorId: floor.id,
            zoneId: zone.id,
          };
        }
      }
    }
    return null;
  };

  const findLandingInSite = (site: RawSite): AccessibleFloorLanding | null => {
    for (const building of [...(site.buildings ?? [])].sort(naturalSort)) {
      const result = findLandingInBuilding(site, building);
      if (result) return result;
    }
    return null;
  };

  if (buildingId) {
    // Scoped lookup. Building IDs are unique across sites; first match
    // terminates the walk. Sites iterated in raw order — lookup is by id,
    // not by sort position, so ordering doesn't matter here.
    for (const site of rawSites) {
      const building = (site.buildings ?? []).find((b) => b.id === buildingId);
      if (!building) continue;
      return findLandingInBuilding(site, building);
    }
    return null;
  }

  // Open lookup: walk sites in natural-sort order, honouring preferSiteId
  // first when present.
  const sortedSites = [...rawSites].sort(naturalSort);
  const preferIdx = preferSiteId ? sortedSites.findIndex((s) => s.id === preferSiteId) : -1;
  // Try preferred site separately only when it's not already at the head —
  // otherwise the main loop reaches it first and a separate try would be
  // wasted work.
  const triedPreferredEarly = preferIdx > 0;
  if (triedPreferredEarly) {
    const result = findLandingInSite(sortedSites[preferIdx]);
    if (result) return result;
  }
  for (let i = 0; i < sortedSites.length; i++) {
    if (triedPreferredEarly && i === preferIdx) continue;
    const result = findLandingInSite(sortedSites[i]);
    if (result) return result;
  }
  return null;
}

/** Find the ancestor chain `[siteId, buildingId?, floorId?, ...]` for the
 * selected key. */
export function findAncestorChain(items: MenuItem[], selectedKey: string): string[] | null {
  const walk = (nodes: MenuItem[], chain: string[]): string[] | null => {
    for (const node of nodes) {
      if (node.key === selectedKey) return [...chain, node.key];
      if (node.children) {
        const found = walk(node.children, [...chain, node.key]);
        if (found) return found;
      }
    }
    return null;
  };
  return walk(items, []);
}

/** Shared open-keys derivations used by every tree view (Devices / Monitor /
 * Studio). Centralizes:
 *
 * 1. `siteAndBuildingKeys` — the "scaffolding" key set used for the "collapse
 *    all rooms" shortcut.
 * 2. `handleCollapseAllRooms` + `collapseAllRoomsVisible` — the shortcut.
 * 3. `openKeysWithSearch` — the effective open-set passed to antd Menu. When
 *    a search filter is active, expands every branch in the (already-pruned)
 *    tree so matched descendants are visible without manual chevron clicks.
 *    User-driven collapses during the search are recorded via
 *    `recordUserClose` and excluded from the auto-expand union, so the row
 *    chevrons remain functional mid-search.
 *
 * The `userClosed` set resets on every `filterValue` change so a new search
 * starts with the full auto-expand again.
 */
export function useTreeOpenKeys({
  items,
  filterValue,
  openKeys,
  setOpenKeys,
}: {
  items: MenuItem[];
  filterValue: string;
  openKeys: string[];
  setOpenKeys: Dispatch<SetStateAction<string[]>>;
}): {
  openKeysWithSearch: string[];
  handleCollapseAllRooms: () => void;
  collapseAllRoomsVisible: boolean;
  recordUserClose: (key: string) => void;
} {
  // Keys the user explicitly collapsed during the current search session.
  // Subtracted from the auto-expand union so chevrons still feel responsive.
  const [userClosed, setUserClosed] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    // Reset on every filter change — a new search starts fresh, and clearing
    // the filter wipes any session-scoped closures so they don't leak into
    // the next search.
    setUserClosed((prev) => (prev.size === 0 ? prev : new Set()));
  }, [filterValue]);

  const recordUserClose = useCallback((key: string) => {
    setUserClosed((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const siteAndBuildingKeys = useMemo(() => {
    const keys = new Set<string>();
    items.forEach((site) => {
      keys.add(String(site.key));
      (site.children ?? []).forEach((b) => keys.add(String(b.key)));
    });
    return keys;
  }, [items]);

  const handleCollapseAllRooms = useCallback(() => {
    setOpenKeys((prev) => prev.filter((k) => siteAndBuildingKeys.has(k)));
  }, [siteAndBuildingKeys, setOpenKeys]);

  const collapseAllRoomsVisible = useMemo(
    () => openKeys.some((k) => !siteAndBuildingKeys.has(k)),
    [openKeys, siteAndBuildingKeys],
  );

  const openKeysWithSearch = useMemo(() => {
    if (!filterValue) return openKeys;
    const autoExpand = collectAllExpandableKeys(items).filter((k) => !userClosed.has(k));
    return Array.from(new Set([...openKeys, ...autoExpand]));
  }, [filterValue, items, openKeys, userClosed]);

  return {
    openKeysWithSearch,
    handleCollapseAllRooms,
    collapseAllRoomsVisible,
    recordUserClose,
  };
}
